/**
 * A Location — a physical place the tenant does work.
 *
 * Promoted from the former `contact_addresses` table, so the shape overlaps
 * heavily with `ContactAddress` (which the contact-scoped address routes still
 * return). The difference is identity and ownership: a location has a
 * user-facing number, a status, and an owning customer reachable from the
 * location rather than the other way round.
 */

export type LocationStatus = 'ACTIVE' | 'INACTIVE';

export interface LocationCustomerSummary {
  id: string;
  displayName: string | null;
  companyName: string | null;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
}

/** A person attached to a location, when the list is asked for them. */
export interface LocationPerson {
  id: string;
  contactId: string;
  role: 'SERVICE' | 'BILLING' | 'APPROVER' | 'OTHER';
  isPrimary: boolean;
  title: string | null;
  receivesNotifications: boolean;
  contact: {
    id: string;
    displayName: string | null;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    primaryPhone: string | null;
    primaryEmail: string | null;
  };
}

export interface Location {
  /** Present only when the list was asked for `includeContacts`. */
  contacts?: LocationPerson[];
  id: string;
  /** User-facing identifier, e.g. `LOC-00001`. */
  locationNumber: string;
  /** Operator's name for the place — "Warehouse", "Unit 4B". */
  nickname: string | null;
  status: LocationStatus;
  /** `MANUAL` or `BACKFILL` (synthesized by a migration; may be a duplicate). */
  source: string;

  /** The owning Contact. */
  customerId: string;
  customer?: LocationCustomerSummary | null;

  /**
   * The stored on-site override. `null` means the location inherits its owner
   * — prefer `resolvedSiteContactId` for display, which already applies that
   * fallback, rather than re-deriving it here.
   */
  siteContactId: string | null;
  resolvedSiteContactId: string;
  hasExplicitSiteContact: boolean;

  title: string | null;
  label: string | null;
  street1: string;
  street2: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;

  isBilling: boolean;
  isService: boolean;
  isPrimary: boolean;

  lat: number | null;
  lng: number | null;
  timezone: string | null;

  taxJurisdictionId: string | null;
  /** Resolved display name, never the raw UUID. */
  taxJurisdiction: string | null;
  isTaxExempt: boolean;
  taxExemptCertificate: string | null;
  taxExemptExpiry: string | null;

  accessNotes: string | null;
  customFields: Record<string, unknown>[];
}

export interface LocationListResponse {
  data: Location[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListLocationsParams {
  /** Ask for the people attached to each location (role, title, switches). */
  includeContacts?: boolean;
  search?: string;
  customerId?: string;
  status?: LocationStatus;
  isService?: boolean;
  limit?: number;
  offset?: number;
}

/** Single-line postal rendering, used by the list and the detail header. */
export function formatLocationAddress(location: Location): string {
  return [
    [location.street1, location.street2].filter(Boolean).join(' '),
    location.city,
    [location.state, location.postalCode].filter(Boolean).join(' '),
  ]
    .filter((part) => part && part.trim().length > 0)
    .join(', ');
}

/**
 * The editable title is the location name. Older locations can fall back to
 * their nickname, label, or street so the name is never blank.
 */
export function locationDisplayName(location: Location): string {
  return (
    location.title?.trim() ||
    location.nickname?.trim() ||
    location.label?.trim() ||
    location.street1
  );
}
